<div class="dialogHeader">
  <div class="titleBlock">
    <h2 class="title">Quick Units</h2>
  </div>
  <button mat-icon-button class="closeButton" type="button" aria-label="Close" [disabled]="isCreating()" (click)="cancel()">
    <mat-icon>close</mat-icon>
  </button>
</div>

<div class="programToggle" role="tablist" aria-label="Program">
  <button type="button" class="programTab" role="tab" [class.active]="program() === 'STK'"
    [attr.aria-selected]="program() === 'STK'" [disabled]="isBusy()" (click)="selectProgram('STK')">
    <mat-icon class="tabIcon">local_shipping</mat-icon>
    Quick Stock
    @if (program() === 'STK' && !isLoading()) {
      <span class="tabCount">{{ units().length }}</span>
    }
  </button>
  <button type="button" class="programTab" role="tab" [class.active]="program() === 'HOT'"
    [attr.aria-selected]="program() === 'HOT'" [disabled]="isBusy()" (click)="selectProgram('HOT')">
    <mat-icon class="tabIcon">bolt</mat-icon>
    Quick Deal
    @if (program() === 'HOT' && !isLoading()) {
      <span class="tabCount">{{ units().length }}</span>
    }
  </button>
</div>

<div class="docLinks" aria-label="Documents">
  <mat-icon class="docIcon">description</mat-icon>
  @for (doc of faqDocuments(); track doc.fileName) {
    <a class="docLink" [href]="faqUrl(doc)" [download]="doc.fileName">{{ doc.label }}</a>
  }
</div>

@if (!infoDismissed()) {
  <div class="notice">
    <mat-icon class="noticeIcon">info</mat-icon>
    <span class="noticeText">
      <span>{{ bannerText }}</span>
      <span class="noticeSubtext">{{ priceNoteText() }}</span>
    </span>
    <button mat-icon-button class="noticeClose" type="button" aria-label="Dismiss" (click)="dismissInfo()">
      <mat-icon>close</mat-icon>
    </button>
  </div>
}

<div class="filterRow">
  <div class="searchBox">
    <mat-icon class="searchIcon">search</mat-icon>
    <input type="text" class="searchInput" [ngModel]="searchTerm()" (ngModelChange)="searchTerm.set($event)"
      [disabled]="isLoading()" placeholder="Search serial, part #, or description"/>
  </div>
  @if (showTypeSelector()) {
    <div class="typeFilter" role="group" aria-label="Product type">
      @for (filter of typeFilters(); track filter.code) {
        <span class="typeChip" [class.active]="typeFilter() === filter.code" [class.locked]="isTypeFilterLocked(filter.code)"
          [matTooltip]="typeFilterTooltip(filter.code)" [attr.aria-disabled]="isTypeFilterLocked(filter.code)"
          (click)="selectTypeFilter(filter.code)">
          {{ filter.label }}
        </span>
      }
    </div>
  }
</div>

@if (errorMessage()) {
  <div class="errorMessage dialogError">
    {{ errorMessage() }}
  </div>
}

<div class="tableWrap">
  <table class="stockTable">
    <thead>
      <tr>
        <th class="checkColumn">
          <input type="checkbox" class="rowCheck" aria-label="Select all visible units"
            [checked]="allVisibleSelected()" [indeterminate]="someVisibleSelected()"
            [disabled]="isBusy() || selectableVisible().length === 0" (change)="toggleAllVisible()"/>
        </th>
        <th class="expandColumn"></th>
        <th class="sortable" (click)="sortBy('serialNumber')">
          <span class="thLabel" [class.sortActive]="sortColumn() === 'serialNumber'">
            Serial
            @if (sortColumn() === 'serialNumber') {
              <mat-icon class="sortIcon">{{ sortAscending() ? 'arrow_drop_up' : 'arrow_drop_down' }}</mat-icon>
            }
          </span>
        </th>
        <th class="sortable" (click)="sortBy('modelNumber')">
          <span class="thLabel" [class.sortActive]="sortColumn() === 'modelNumber'">
            Part #
            @if (sortColumn() === 'modelNumber') {
              <mat-icon class="sortIcon">{{ sortAscending() ? 'arrow_drop_up' : 'arrow_drop_down' }}</mat-icon>
            }
          </span>
        </th>
        <th class="sortable" (click)="sortBy('description')">
          <span class="thLabel" [class.sortActive]="sortColumn() === 'description'">
            Description
            @if (sortColumn() === 'description') {
              <mat-icon class="sortIcon">{{ sortAscending() ? 'arrow_drop_up' : 'arrow_drop_down' }}</mat-icon>
            }
          </span>
        </th>
        <th class="sortable" (click)="sortBy('productType')">
          <span class="thLabel" [class.sortActive]="sortColumn() === 'productType'">
            Type
            @if (sortColumn() === 'productType') {
              <mat-icon class="sortIcon">{{ sortAscending() ? 'arrow_drop_up' : 'arrow_drop_down' }}</mat-icon>
            }
          </span>
        </th>
        <th class="sortable priceColumn" (click)="sortBy('unitPrice')">
          <span class="thLabel" [class.sortActive]="sortColumn() === 'unitPrice'">
            {{ priceColumnLabel() }}
            @if (sortColumn() === 'unitPrice') {
              <mat-icon class="sortIcon">{{ sortAscending() ? 'arrow_drop_up' : 'arrow_drop_down' }}</mat-icon>
            }
          </span>
        </th>
        @if (showNetPrice()) {
          <th class="sortable priceColumn" (click)="sortBy('netPrice')">
            <span class="thLabel" [class.sortActive]="sortColumn() === 'netPrice'">
              Net Price
              @if (sortColumn() === 'netPrice') {
                <mat-icon class="sortIcon">{{ sortAscending() ? 'arrow_drop_up' : 'arrow_drop_down' }}</mat-icon>
              }
            </span>
          </th>
        }
      </tr>
    </thead>
    <tbody>
      @if (isLoading()) {
        <tr>
          <td [attr.colspan]="showNetPrice() ? 8 : 7" class="emptyCell">
            <mat-progress-spinner diameter="28" mode="indeterminate" class="loadingSpinner"></mat-progress-spinner>
            Loading units...
          </td>
        </tr>
      } @else if (filtered().length === 0) {
        <tr>
          <td [attr.colspan]="showNetPrice() ? 8 : 7" class="emptyCell">
            @if (units().length === 0) {
              No {{ programLabel() }} units are available for your office right now.
            } @else {
              No {{ programLabel() }} units match your search.
            }
          </td>
        </tr>
      } @else {
        @for (unit of filtered(); track unit.productGuid) {
          <tr class="unitRow" [class.rowSelected]="isSelected(unit)" [class.rowLocked]="isLocked(unit)"
            [title]="isLocked(unit) ? mixingMessage : ''" (click)="toggleUnit(unit)">
            <td class="checkColumn">
              <input type="checkbox" class="rowCheck" [checked]="isSelected(unit)" [disabled]="isBusy() || isLocked(unit)"
                [attr.aria-label]="'Select ' + (unit.serialNumber || unit.modelNumber)"
                (click)="$event.stopPropagation()" (change)="toggleUnit(unit)"/>
            </td>
            <td class="expandColumn">
              <button mat-icon-button class="expandToggle" type="button"
                [matTooltip]="isExpanded(unit) ? 'Hide model details' : 'Show model details'"
                [attr.aria-expanded]="isExpanded(unit)" (click)="toggleDecoder($event, unit)">
                <mat-icon class="expandIcon">{{ isExpanded(unit) ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>
            </td>
            <td class="mono">{{ unit.serialNumber || '—' }}</td>
            <td class="mono">{{ unit.modelNumber }}</td>
            <td>{{ unit.description }}</td>
            <td><span [class]="'badge ' + typeBadgeClass(unit.productType)">{{ typeLabel(unit.productType) }}</span></td>
            <td class="priceCell">{{ unit.unitPrice | currency }}</td>
            @if (showNetPrice()) {
              <td class="priceCell netPriceCell">{{ unit.netPrice != null ? (unit.netPrice | currency) : '—' }}</td>
            }
          </tr>
          @if (isExpanded(unit)) {
            <tr class="decoderRow">
              <td [attr.colspan]="showNetPrice() ? 8 : 7">
                @switch (decoderState(unit)) {
                  @case ('loaded') {
                    <decoder-table [decoderData]="decoderFor(unit)!" [title]="'Decoder For ' + unit.modelNumber"></decoder-table>
                  }
                  @case ('empty') {
                    <span class="decoderLoading">{{ noDecoderText }}</span>
                  }
                  @case ('error') {
                    <div class="errorMessage">{{ decoderError() }}</div>
                  }
                  @default {
                    <span class="decoderLoading">Loading model details...</span>
                  }
                }
              </td>
            </tr>
          }
        }
      }
    </tbody>
  </table>
</div>

<div class="dialogFooter">
  <div class="footerSummary">
    <div class="footerStat">
      <span class="footerStatLabel">Selected</span>
      <span class="footerStatValue">{{ selectedCount() }} {{ selectedCount() === 1 ? 'unit' : 'units' }}</span>
    </div>
    <div class="footerStat">
      <span class="footerStatLabel">{{ subtotalLabel() }}</span>
      <span class="footerStatValue">{{ subtotal() | currency }}</span>
    </div>
    @if (showNetPrice()) {
      <div class="footerStat">
        <span class="footerStatLabel">Net subtotal</span>
        <span class="footerStatValue">{{ netSubtotal() | currency }}</span>
      </div>
    }
  </div>
  <div class="footerActions">
    <button class="warn-button" matButton="elevated" type="button" [disabled]="isCreating()" (click)="cancel()">Cancel</button>
    <button class="primary-button createButton" matButton="elevated" type="button"
      [disabled]="selectedCount() === 0 || isBusy()" (click)="createQuote()">
      @if (isCreating()) {
        <mat-progress-spinner diameter="18" mode="indeterminate" class="buttonSpinner"></mat-progress-spinner>
      }
      Create Quote for These Products
    </button>
  </div>
</div>
